<template>
    <div class="mt-3">
        <h1 class="text-3xl text-center">
            Total tenants
            <strong class="text-theme-primary">{{ itemsNumber }}</strong>
        </h1>

        <div class="mt-3">
            <tenants-filters v-model="query" @enter="reloadItems" />
            <div class="flex justify-center mt-3">
                <app-button relief @click="reloadItems">
                    Consultar 🔎
                </app-button>
            </div>
        </div>

        <div
            class="flex flex-col lg:flex-row lg:items-center lg:justify-end gap-4 my-4"
        >
            <div
                class="flex flex-wrap justify-center lg:justify-end items-center gap-4"
            >
                <list-settings-button
                    :value="{ pageSize, sortConfig }"
                    :sort-options="sortOptions"
                    @change="onListSettingsChange"
                />

                <custom-pagination
                    v-model="currentPage"
                    :length="pagesNumber"
                    @input="listItems()"
                />
            </div>
        </div>

        <div class="w-full mt-2">
            <custom-sortable-table
                :fields="$data.$fields"
                :items="itemsCurrent"
                :per-page="itemsCurrent.length"
                :selectable="false"
                :row-actions="rowActions"
                responsive
                hover
                class="w-full"
                items-empty-message="Nuestro sistema NO encuentra resultados. Intenta modificar los filtros de búsqueda."
                @row-action="onRowAction"
            >
                <template #cell(id)="data">
                    <div class="flex items-center gap-2">
                        <router-link
                            :to="`/app/tenants/detail/${data.item.id}`"
                            class="text-theme-primary"
                            title="Ver detalle"
                        >
                            <chevron-right-icon class="w-4 h-4" />
                        </router-link>
                        <uuid-display :value="data.item.id" />
                    </div>
                </template>
                <template #cell(ownerEmail)="data">
                    <span
                        v-if="data.item.ownerEmail"
                        :title="data.item.ownerName"
                    >
                        {{ data.item.ownerEmail }}
                    </span>
                </template>
                <template #cell(planName)="data">
                    {{ data.item.planName || "" }}
                </template>
                <template #cell(subscriptionStatus)="data">
                    <subscription-status-badge
                        v-if="data.item.subscriptionStatus"
                        :status="data.item.subscriptionStatus"
                    />
                    <span v-else class="text-theme-muted">
                        {{ $data.$noSubscriptionLabel }}
                    </span>
                </template>
                <template #cell(currentPeriodEnd)="data">
                    <span v-if="data.item.currentPeriodEnd">
                        {{ data.item.currentPeriodEnd | dateSimple }}
                    </span>
                </template>
                <template #cell(createdAt)="data">
                    {{ data.item.createdAt | date }}
                </template>
            </custom-sortable-table>
        </div>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters, mapState } from "vuex";

import { noSubscriptionLabel } from "@/common/fields/platform-subscription-codes";
import { buildQueryFromUrl } from "@/common/lib/url-query-filters";

import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import TenantsFilters from "@/components/tenants/TenantsFilters.vue";
import CustomSortableTable from "@/components/utils/CustomSortableTable.vue";
import SubscriptionStatusBadge from "@/components/utils/SubscriptionStatusBadge.vue";
import UuidDisplay from "@/components/utils/UuidDisplay.vue";

export default {
    name: "TenantsIndex",
    components: {
        CustomSortableTable,
        ChevronRightIcon,
        SubscriptionStatusBadge,
        TenantsFilters,
        UuidDisplay
    },
    mixins: [urlQuerySyncMixin],
    data: () => ({
        itemsCurrent: [],
        currentPage: 1,
        pagesNumber: 0,
        itemsNumber: 0,
        pageSize: 24,
        sortConfig: { orderBy: "createdAt", orderType: "DESC" },
        query: {},
        $noSubscriptionLabel: noSubscriptionLabel,
        $fields: [
            { key: "id", label: "ID", sortable: false },
            { key: "name", label: "Nombre", sortable: true },
            { key: "slug", label: "Slug", sortable: true },
            { key: "ownerEmail", label: "Dueño", sortable: false },
            { key: "planName", label: "Plan", sortable: false },
            { key: "subscriptionStatus", label: "Estado", sortable: false },
            { key: "currentPeriodEnd", label: "Vence", sortable: false },
            { key: "createdAt", label: "Creado", sortable: false }
        ],
        $urlPaginationKey: "page",
        $urlSortKey: "sort",
        $urlFilterSchema: [
            { key: "name", type: "string" },
            { key: "id", type: "string" },
            { key: "planCode", type: "string" },
            { key: "businessType", type: "string" },
            { key: "subscriptionType", type: "string" },
            { key: "subscriptionStatus", type: "string" },
            { key: "lastInvoiceStatus", type: "string" },
            { key: "createdAt", type: "range" },
            { key: "currentPeriodEnd", type: "range" }
        ]
    }),
    computed: {
        ...mapState("auth", ["tenantId", "globalPermissions"]),
        ...mapGetters("control", ["backgroundColor"]),
        rowActions() {
            const actions = [];
            if (this.$isMultitenant && this.$franchiseDomain) {
                actions.push({
                    key: "view-catalog",
                    icon: "🛍️",
                    label: "Ver catálogo"
                });
            }
            if (
                !this.tenantId &&
                this.$ability.hasPermission("tenant:Select")
            ) {
                actions.push({
                    key: "select-tenant",
                    icon: "🏢",
                    label: "Seleccionar tenant"
                });
            }
            return actions;
        },
        sortOptions() {
            return [
                { label: "📆 Creación reciente", value: "createdAt:DESC" },
                { label: "📆 Creación antigua", value: "createdAt:ASC" },
                { label: "Nombre A-Z", value: "name:ASC" },
                { label: "Nombre Z-A", value: "name:DESC" },
                { label: "Vence pronto", value: "currentPeriodEnd:ASC" }
            ];
        }
    },
    watch: {
        "$route.fullPath"(newPath, oldPath) {
            if (newPath === oldPath) return;
            this.query = {
                ...buildQueryFromUrl(
                    this.$route.query,
                    this.$data.$urlFilterSchema
                )
            };
            this.reloadItems();
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("tenant:List", "/app"))) return;
        this.listItems(true);
    },
    methods: {
        ...mapActions("tenants", ["listTenantsPaginated"]),
        ...mapActions("auth", ["applyTenantContext"]),
        async listItems(complete = false) {
            this.$loading.show();
            try {
                const { items, pagination } = await this.listTenantsPaginated({
                    ...this.query,
                    complete,
                    itemsByPage: this.pageSize,
                    orderBy: this.sortConfig.orderBy,
                    orderType: this.sortConfig.orderType,
                    page: this.currentPage
                });
                this.itemsCurrent = items;
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItems() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        onListSettingsChange({ pageSize, sortConfig }) {
            this.pageSize = pageSize;
            if (sortConfig) this.sortConfig = sortConfig;
            this.reloadItems();
        },
        async onRowAction({ action, item }) {
            if (action === "view-catalog") {
                if (!item.slug) return;
                window.open(
                    `https://${item.slug}.${this.$franchiseDomain}`,
                    "_blank"
                );
                return;
            }
            if (action === "select-tenant") {
                const result = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: `¿Seleccionar "${item.name}"?`,
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: "Seleccionar",
                    cancelButtonText: "Cancelar"
                });
                if (!result.isConfirmed) return;
                this.$loading.show();
                try {
                    await this.applyTenantContext({
                        tenantId: item.id,
                        tenantSlug: item.slug
                    });
                    this.$sound.play();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: `Tenant "${item.name}" seleccionado`,
                        icon: "success",
                        showCloseButton: true,
                        timer: 3000
                    });
                    this.$router.push("/app");
                    this.$loading.hide();
                } catch (error) {
                    this.$loading.hide();
                    this.$sound.play("error");
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        }
    }
};
</script>
